import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import MDSpinner from "react-md-spinner";
import { FaMapMarkerAlt } from "react-icons/fa";

import Title from "../../components/Title";
import AgriDateTime from "../../components/AgriDateTime";
import { fetchScheduledMaintenances } from "../FieldApp/fieldAppApi";
import FieldAppTelemetryMapPanel from "../FieldApp/FieldAppTelemetryMapPanel";
import OperationFleetShiftsPanel from "./OperationFleetShiftsPanel";
import {
  buildMaintenanceByEquipmentMap,
  fetchFleetMeterSummary,
  filterFleetRowsByMeterTab,
  getMaintenanceRemaining,
  getMaintenanceTargetValue,
  maintenanceDetailPath,
  mergeFleetPresetRows,
  maintenanceMeterSuffix,
  sortFleetRows,
} from "./operationFleetApi";

import "./OperationFleetSummary.css";

const READING_MAP_FOCUS_WINDOW_MS = 30 * 60 * 1000;

function applyTimeToDate(date, timeValue) {
  if (!date || !timeValue) return date;
  const next = new Date(date);
  const [hours, minutes] = String(timeValue).split(":");
  next.setHours(Number(hours) || 0, Number(minutes) || 0, 0, 0);
  return next;
}

function formatMeterValue(value, suffix) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return `${num.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })} ${suffix}`;
}

function formatWorkedValue(value, meterTab) {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return "—";
  return meterTab === "odometer"
    ? formatMeterValue(num, "km")
    : formatMeterValue(num, "h");
}

function formatCombinedWorked(hours, km) {
  const hourLabel = formatWorkedValue(hours, "hour_meter");
  const kmLabel = formatWorkedValue(km, "odometer");
  if (hourLabel === "—" && kmLabel === "—") return "—";
  if (hourLabel === "—") return kmLabel;
  if (kmLabel === "—") return hourLabel;
  return `${hourLabel} / ${kmLabel}`;
}

function parseReadingDate(value) {
  if (!value) return null;
  const raw = String(value);
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    return new Date(
      Number(dateOnly[1]),
      Number(dateOnly[2]) - 1,
      Number(dateOnly[3])
    );
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function formatReadingDate(value) {
  const date = parseReadingDate(value);
  if (!date) return "";
  return date.toLocaleDateString("pt-BR");
}

function formatReadingDateTime(value) {
  const date = parseReadingDate(value);
  if (!date) return "";
  const day = date.toLocaleDateString("pt-BR");
  if (
    date.getHours() === 0 &&
    date.getMinutes() === 0 &&
    date.getSeconds() === 0
  ) {
    return day;
  }
  return `${day} ${date.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

function hasValidCoords(lat, lng) {
  const latitude = Number(lat);
  const longitude = Number(lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false;
  if (Math.abs(latitude) < 0.000001 && Math.abs(longitude) < 0.000001) {
    return false;
  }
  return Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180;
}

function dayKeyFromDate(date) {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function positiveId(value) {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
}

function buildReadingTrackingView(reading, t) {
  if (!reading) return null;
  const date = parseReadingDate(reading.at);
  if (!date) return null;
  const dayKey = dayKeyFromDate(date);
  if (!dayKey) return null;

  const centerMs = date.getTime();
  const operatorId = positiveId(reading.operatorId);
  const equipmentId = positiveId(reading.equipmentId);
  const machineIdent = reading.machineIdent || "";
  const hasPoint = hasValidCoords(reading.lat, reading.lng);

  return {
    dayKey,
    focusStart: new Date(centerMs - READING_MAP_FOCUS_WINDOW_MS).toISOString(),
    focusEnd: new Date(centerMs + READING_MAP_FOCUS_WINDOW_MS).toISOString(),
    mapFullDay: true,
    silentFocus: true,
    label: t("Última leitura — {{machine}}", {
      machine: machineIdent || t("Máquina"),
    }),
    mapCenter: hasPoint
      ? {
          lat: Number(reading.lat),
          lng: Number(reading.lng),
          time: date.toISOString(),
          title: t("Local da leitura"),
        }
      : null,
    operatorIds: operatorId ? [operatorId] : [],
    equipmentIds: equipmentId ? [equipmentId] : [],
    machineIdents: machineIdent ? [machineIdent] : [],
    sessionUuid: reading.sessionUuid || null,
    token: Date.now(),
  };
}

function ReadingDateButton({ reading, t, onOpenTracking }) {
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const when = formatReadingDate(reading && reading.at);
  const whenFull = formatReadingDateTime(reading && reading.at);
  const trackingView = buildReadingTrackingView(reading, t);
  const canOpenTracking = Boolean(trackingView && onOpenTracking);
  const hasPoint = hasValidCoords(reading && reading.lat, reading && reading.lng);

  useEffect(() => {
    if (!open) return undefined;
    function handlePointerDown(event) {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    function handleScroll() {
      setOpen(false);
    }
    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [open]);

  if (!when) return null;

  function togglePopover() {
    if (open) {
      setOpen(false);
      return;
    }
    const rect = buttonRef.current && buttonRef.current.getBoundingClientRect();
    if (rect) {
      setPanelStyle({ top: rect.bottom + 6, left: rect.left });
    }
    setOpen(true);
  }

  function openTracking(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!canOpenTracking) return;
    setOpen(false);
    onOpenTracking(trackingView);
  }

  return (
    <span className="operation-fleet-summary__reading-date-wrap" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className="operation-fleet-summary__reading-at"
        onClick={togglePopover}
        title={t("Ver horário e localização da leitura")}
      >
        {when}
      </button>
      {open ? (
        <span
          className="operation-fleet-summary__reading-popover"
          style={panelStyle}
        >
          <span className="operation-fleet-summary__reading-popover-time">
            {whenFull || when}
          </span>
          {canOpenTracking ? (
            <button
              type="button"
              className="operation-fleet-summary__reading-map"
              onClick={openTracking}
              title={t("Abrir no rastreamento")}
            >
              <FaMapMarkerAlt aria-hidden="true" />
              {t("Abrir no rastreamento")}
            </button>
          ) : null}
          {!hasPoint ? (
            <span className="operation-fleet-summary__reading-map-empty">
              {t("Localização não registrada")}
            </span>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}

function readingLine(value, suffix, reading, t, onOpenTracking) {
  const formatted = formatMeterValue(value, suffix);
  if (formatted === "—") return null;
  return (
    <span className="operation-fleet-summary__reading">
      <span className="operation-fleet-summary__reading-value">{formatted}</span>
      <ReadingDateButton
        reading={reading}
        t={t}
        onOpenTracking={onOpenTracking}
      />
    </span>
  );
}

function hourReading(row) {
  return {
    at: row.currentHourMeterAt,
    lat: row.currentHourMeterLat,
    lng: row.currentHourMeterLng,
    operatorId: row.currentHourMeterOperatorId,
    sessionUuid: row.currentHourMeterSessionUuid,
    machineIdent: row.machineIdent,
    equipmentId: row.equipmentId,
  };
}

function odometerReading(row) {
  return {
    at: row.currentOdometerAt,
    lat: row.currentOdometerLat,
    lng: row.currentOdometerLng,
    operatorId: row.currentOdometerOperatorId,
    sessionUuid: row.currentOdometerSessionUuid,
    machineIdent: row.machineIdent,
    equipmentId: row.equipmentId,
  };
}

function formatCurrentReading(row, meterTab, t, onOpenTracking) {
  if (meterTab === "odometer") {
    return (
      readingLine(
        row.currentOdometerKm,
        "km",
        odometerReading(row),
        t,
        onOpenTracking
      ) || "—"
    );
  }
  if (meterTab === "hour_meter") {
    return (
      readingLine(
        row.currentHourMeter,
        "h",
        hourReading(row),
        t,
        onOpenTracking
      ) || "—"
    );
  }
  const hours = readingLine(
    row.currentHourMeter,
    "h",
    hourReading(row),
    t,
    onOpenTracking
  );
  const km = readingLine(
    row.currentOdometerKm,
    "km",
    odometerReading(row),
    t,
    onOpenTracking
  );
  if (row.meterType === "odometer") return km || "—";
  if (row.meterType === "both" && hours && km) {
    return (
      <span className="operation-fleet-summary__reading-pair">
        {hours}
        <span className="operation-fleet-summary__reading-sep">·</span>
        {km}
      </span>
    );
  }
  if (hours) return hours;
  return km || "—";
}

function periodWorkedLabel(period, meterTab) {
  if (!period) return "—";
  if (meterTab === "both") {
    return formatCombinedWorked(period.workedHours, period.workedKm);
  }
  return formatWorkedValue(
    meterTab === "odometer" ? period.workedKm : period.workedHours,
    meterTab
  );
}

function OperationFleetSummary({ match, t }) {
  const farmId = match.params.farm_id;
  const [meterTab, setMeterTab] = useState("both");
  const [sortPreset, setSortPreset] = useState("month");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [presetRows, setPresetRows] = useState([]);
  const [customRange, setCustomRange] = useState({
    from: null,
    to: null,
    fromTime: "00:00",
    toTime: "23:59",
  });
  const [customRows, setCustomRows] = useState([]);
  const [customLoading, setCustomLoading] = useState(false);
  const [scheduledMaintenances, setScheduledMaintenances] = useState([]);
  const [trackingView, setTrackingView] = useState(null);
  const [shiftsMachine, setShiftsMachine] = useState(null);

  const loadPresets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, maintenanceData] = await Promise.all([
        fetchFleetMeterSummary(farmId),
        fetchScheduledMaintenances(farmId, { limit: 500 }, { allMachines: true }),
      ]);
      setPresetRows(mergeFleetPresetRows(data.presets || {}));
      setScheduledMaintenances(maintenanceData.results || []);
    } catch (loadError) {
      console.error(loadError);
      setPresetRows([]);
      setScheduledMaintenances([]);
      setError(t("Falha ao carregar resumo das máquinas"));
    } finally {
      setLoading(false);
    }
  }, [farmId, t]);

  useEffect(() => {
    document.title = `${t("Resumo das máquinas")} - Agriconnected`;
    loadPresets();
  }, [loadPresets, t]);

  const loadCustomRange = useCallback(async () => {
    if (!customRange.from || !customRange.to) return;
    const dateFrom = applyTimeToDate(customRange.from, customRange.fromTime);
    const dateTo = applyTimeToDate(customRange.to, customRange.toTime);
    setCustomLoading(true);
    setError(null);
    try {
      const data = await fetchFleetMeterSummary(farmId, {
        dateFrom: dateFrom.toISOString(),
        dateTo: dateTo.toISOString(),
      });
      setCustomRows(
        (data.machines || []).map((machine) => ({
          ...machine,
          periods: {
            custom: {
              workedHours: machine.workedHours,
              workedKm: machine.workedKm,
              sessionCount: machine.sessionCount,
            },
          },
        }))
      );
      setSortPreset("custom");
    } catch (loadError) {
      console.error(loadError);
      setCustomRows([]);
      setError(t("Falha ao carregar período personalizado"));
    } finally {
      setCustomLoading(false);
    }
  }, [
    customRange.from,
    customRange.to,
    customRange.fromTime,
    customRange.toTime,
    farmId,
    t,
  ]);

  const baseRows =
    sortPreset === "custom" && customRows.length ? customRows : presetRows;

  const maintenanceByEquipment = useMemo(
    () => buildMaintenanceByEquipmentMap(scheduledMaintenances, baseRows, meterTab),
    [scheduledMaintenances, baseRows, meterTab]
  );

  const filteredRows = useMemo(
    () =>
      sortFleetRows(
        filterFleetRowsByMeterTab(baseRows, meterTab),
        meterTab,
        sortPreset,
        { maintenanceByEquipment }
      ),
    [baseRows, meterTab, sortPreset, maintenanceByEquipment]
  );

  const currentLabel =
    meterTab === "odometer"
      ? t("Última leitura (km)")
      : meterTab === "hour_meter"
        ? t("Última leitura (h)")
        : t("Última leitura");
  const workedDayLabel =
    meterTab === "odometer"
      ? t("Km — hoje")
      : meterTab === "hour_meter"
        ? t("Horas — hoje")
        : t("Hoje");
  const workedWeekLabel =
    meterTab === "odometer"
      ? t("Km — 7 dias")
      : meterTab === "hour_meter"
        ? t("Horas — 7 dias")
        : t("7 dias");
  const workedMonthLabel =
    meterTab === "odometer"
      ? t("Km — 30 dias")
      : meterTab === "hour_meter"
        ? t("Horas — 30 dias")
        : t("30 dias");
  const workedCustomLabel =
    meterTab === "odometer"
      ? t("Km — período")
      : meterTab === "hour_meter"
        ? t("Horas — período")
        : t("Período");
  const remainingMaintenanceHeader =
    meterTab === "odometer"
      ? {
          line1: t("Km até a"),
          line2: t("próxima manutenção"),
        }
      : meterTab === "hour_meter"
        ? {
            line1: t("Horas até a"),
            line2: t("próxima manutenção"),
          }
        : {
            line1: t("Até a"),
            line2: t("próxima manutenção"),
          };
  const targetMaintenanceHeader = {
    line1: t("Próxima"),
    line2: t("manutenção"),
  };
  const tableColumnCount = (customRows.length ? 7 : 6) + 2;

  const renderMaintenanceRemaining = (row) => {
    const maintenance = maintenanceByEquipment.get(String(row.equipmentId));
    if (!maintenance) return "—";

    const remaining = getMaintenanceRemaining(maintenance, row, meterTab);
    if (!remaining || !Number.isFinite(remaining.value)) return "—";

    const formatted = formatMeterValue(
      remaining.value,
      maintenanceMeterSuffix(maintenance, meterTab)
    );
    const label = remaining.overdue
      ? t("Vencida há {{value}}", { value: formatted })
      : formatted;
    const detailPath = maintenanceDetailPath(
      farmId,
      row.equipmentId,
      maintenance.id
    );

    return (
      <Link
        to={detailPath}
        className={
          remaining.overdue
            ? "operation-fleet-summary__maintenance-link operation-fleet-summary__maintenance-link--overdue"
            : "operation-fleet-summary__maintenance-link"
        }
        title={
          maintenance.name
            ? t("Ver manutenção: {{name}}", { name: maintenance.name })
            : t("Ver detalhes da manutenção")
        }
      >
        {label}
      </Link>
    );
  };

  const renderMaintenanceTarget = (row) => {
    const maintenance = maintenanceByEquipment.get(String(row.equipmentId));
    if (!maintenance) return "—";

    const target = getMaintenanceTargetValue(maintenance, meterTab);
    if (target == null || !Number.isFinite(Number(target))) return "—";

    const remaining = getMaintenanceRemaining(maintenance, row, meterTab);
    const detailPath = maintenanceDetailPath(
      farmId,
      row.equipmentId,
      maintenance.id
    );

    return (
      <Link
        to={detailPath}
        className={
          remaining && remaining.overdue
            ? "operation-fleet-summary__maintenance-link operation-fleet-summary__maintenance-link--overdue"
            : "operation-fleet-summary__maintenance-link"
        }
        title={
          maintenance.name
            ? t("Ver manutenção: {{name}}", { name: maintenance.name })
            : t("Ver detalhes da manutenção")
        }
      >
        {formatMeterValue(target, maintenanceMeterSuffix(maintenance, meterTab))}
      </Link>
    );
  };

  return (
    <div className="operation-fleet-summary">
      <Title title={t("Resumo das máquinas")} />

      <p className="operation-fleet-summary__intro">
        {t(
          "Visão de todas as máquinas da fazenda: última leitura registrada do horímetro ou hodômetro, com a data dessa leitura, e quanto cada uma trabalhou hoje, na última semana e no último mês. A lista prioriza manutenções vencidas, depois as mais próximas do vencimento, e em seguida ordena pela máquina que mais trabalhou no período selecionado."
        )}
      </p>

      <div className="operation-fleet-summary__toolbar">
        <div className="operation-fleet-summary__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={meterTab === "both"}
            className={
              meterTab === "both"
                ? "operation-fleet-summary__tab operation-fleet-summary__tab--active"
                : "operation-fleet-summary__tab"
            }
            onClick={() => setMeterTab("both")}
          >
            {t("Ambos (h e km)")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={meterTab === "hour_meter"}
            className={
              meterTab === "hour_meter"
                ? "operation-fleet-summary__tab operation-fleet-summary__tab--active"
                : "operation-fleet-summary__tab"
            }
            onClick={() => setMeterTab("hour_meter")}
          >
            {t("Horímetro (horas)")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={meterTab === "odometer"}
            className={
              meterTab === "odometer"
                ? "operation-fleet-summary__tab operation-fleet-summary__tab--active"
                : "operation-fleet-summary__tab"
            }
            onClick={() => setMeterTab("odometer")}
          >
            {t("Hodômetro (km)")}
          </button>
        </div>

        <label>
          {t("Ordenar por")}{" "}
          <select
            value={sortPreset}
            onChange={(event) => setSortPreset(event.target.value)}
          >
            <option value="day">{t("Hoje")}</option>
            <option value="week">{t("Últimos 7 dias")}</option>
            <option value="month">{t("Últimos 30 dias")}</option>
            {customRows.length ? (
              <option value="custom">{t("Período personalizado")}</option>
            ) : null}
          </select>
        </label>
      </div>

      <div className="operation-fleet-summary__custom">
        <label>
          {t("De")}
          <AgriDateTime
            label=""
            datetimeformat="true"
            selectedDate={customRange.from}
            time={customRange.fromTime}
            handleDateChange={(value) =>
              setCustomRange((current) => ({ ...current, from: value }))
            }
            handleTimeChange={(event) =>
              setCustomRange((current) => ({
                ...current,
                fromTime: event.target.value,
              }))
            }
          />
        </label>
        <label>
          {t("Até")}
          <AgriDateTime
            label=""
            datetimeformat="true"
            selectedDate={customRange.to}
            time={customRange.toTime}
            handleDateChange={(value) =>
              setCustomRange((current) => ({ ...current, to: value }))
            }
            handleTimeChange={(event) =>
              setCustomRange((current) => ({
                ...current,
                toTime: event.target.value,
              }))
            }
          />
        </label>
        <button
          type="button"
          className="btn btn-default btn-sm"
          onClick={loadCustomRange}
          disabled={customLoading || !customRange.from || !customRange.to}
        >
          {customLoading ? t("Carregando…") : t("Aplicar período")}
        </button>
      </div>

      {error ? <p className="operation-fleet-summary__error">{error}</p> : null}

      {loading ? (
        <MDSpinner singleColor="#1abb9c" size={36} borderSize={4} />
      ) : (
        <div className="operation-fleet-summary__table-wrap">
          <table className="table table-striped jambo_table operation-fleet-summary__table">
            <thead>
              <tr>
                <th>{t("Máquina")}</th>
                <th>{currentLabel}</th>
                <th
                  className="operation-fleet-summary__th-stacked"
                  title={t(
                    "Quanto falta até a próxima manutenção agendada. Clique no valor para abrir os detalhes."
                  )}
                >
                  <span className="operation-fleet-summary__th-stacked-line">
                    {remainingMaintenanceHeader.line1}
                  </span>
                  <span className="operation-fleet-summary__th-stacked-line">
                    {remainingMaintenanceHeader.line2}
                  </span>
                </th>
                <th
                  className="operation-fleet-summary__th-stacked"
                  title={t(
                    "Leitura do medidor em que a próxima manutenção deve ser feita."
                  )}
                >
                  <span className="operation-fleet-summary__th-stacked-line">
                    {targetMaintenanceHeader.line1}
                  </span>
                  <span className="operation-fleet-summary__th-stacked-line">
                    {targetMaintenanceHeader.line2}
                  </span>
                </th>
                <th>{workedDayLabel}</th>
                <th>{workedWeekLabel}</th>
                <th>{workedMonthLabel}</th>
                {customRows.length ? <th>{workedCustomLabel}</th> : null}
                <th>{t("Turnos")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={tableColumnCount}>
                    {t("Nenhuma máquina encontrada para este grupo de medidor.")}
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => {
                  const periods = row.periods || {};
                  const sessions =
                    (periods[sortPreset] && periods[sortPreset].sessionCount) || 0;
                  return (
                    <tr key={row.equipmentId || row.machineIdent}>
                      <td>
                        <strong>{row.machineIdent || "—"}</strong>
                      </td>
                      <td>
                        {formatCurrentReading(
                          row,
                          meterTab,
                          t,
                          setTrackingView
                        )}
                      </td>
                      <td>{renderMaintenanceRemaining(row)}</td>
                      <td>{renderMaintenanceTarget(row)}</td>
                      <td>{periodWorkedLabel(periods.day, meterTab)}</td>
                      <td>{periodWorkedLabel(periods.week, meterTab)}</td>
                      <td>{periodWorkedLabel(periods.month, meterTab)}</td>
                      {customRows.length ? (
                        <td>{periodWorkedLabel(periods.custom, meterTab)}</td>
                      ) : null}
                      <td>
                        {row.machineIdent ? (
                          <button
                            type="button"
                            className={
                              sessions > 0
                                ? "operation-fleet-summary__shifts-btn"
                                : "operation-fleet-summary__shifts-btn operation-fleet-summary__shifts-btn--empty"
                            }
                            title={t("Ver planilha de turnos da máquina")}
                            onClick={() =>
                              setShiftsMachine({
                                machineIdent: row.machineIdent,
                                equipmentId: row.equipmentId,
                                meterType: row.meterType,
                              })
                            }
                          >
                            {sessions > 0 ? sessions : "—"}
                          </button>
                        ) : sessions > 0 ? (
                          sessions
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {shiftsMachine ? (
        <OperationFleetShiftsPanel
          farmId={farmId}
          machine={shiftsMachine}
          onClose={() => setShiftsMachine(null)}
          t={t}
        />
      ) : null}

      {trackingView ? (
        <FieldAppTelemetryMapPanel
          farmId={farmId}
          selection={{
            operatorIds: trackingView.operatorIds || [],
            equipmentIds: trackingView.equipmentIds || [],
            machineIdents: trackingView.machineIdents || [],
            allMachines: false,
          }}
          view={trackingView}
          onClose={() => setTrackingView(null)}
          t={t}
        />
      ) : null}
    </div>
  );
}

export default withTranslation()(OperationFleetSummary);
